import { useEffect, useState } from "react";
import { Plus, Trash, Buildings, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function PropertyTypes() {
  const [items, setItems] = useState(null);
  const [draft, setDraft] = useState("");
  const [counts, setCounts] = useState({});

  const load = () => api.get("/settings/property_types").then((r) => setItems(r.data?.items || [])).catch(() => setItems([]));
  useEffect(() => {
    load();
    api.get("/jobs").then((r) => {
      const c = {};
      r.data.forEach((j) => { c[j.property_type] = (c[j.property_type] || 0) + 1; });
      setCounts(c);
    }).catch(() => {});
  }, []);

  const persist = async (next) => {
    setItems(next);
    try { await api.put("/settings/property_types", { items: next }); toast.success("Property types saved"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); load(); }
  };

  const add = (e) => {
    e.preventDefault();
    const v = draft.trim();
    if (!v) return;
    if (items.includes(v)) { toast.error("Already in the list"); return; }
    persist([...items, v]);
    setDraft("");
  };

  if (!items) return <div className="text-muted-foreground font-mono text-sm animate-pulse">Loading property types…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="property-types-page">
      <PageHeader overline="Valuation Jobs" title="Property Types" subtitle="The master list used to tag jobs and drive report-format selection" />

      <Card className="p-6 border-border">
        <form onSubmit={add} className="flex flex-wrap gap-2" data-testid="property-type-form">
          <Input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="e.g. Warehouse / Godown" className="flex-1 min-w-[200px]" data-testid="property-type-input" />
          <Button type="submit" className="rounded-full bg-primary hover:bg-[#113A64]" data-testid="add-property-type-btn"><Plus size={16} className="mr-1" /> Add Type</Button>
        </form>

        {items.length === 0 ? (
          <div className="mt-5"><EmptyState icon={Buildings}>No property types yet. Add the ones your reports cover.</EmptyState></div>
        ) : (
          <div className="mt-5 space-y-2">
            {items.map((t) => (
              <div key={t} className="flex items-center justify-between gap-3 p-3 rounded-md bg-muted border border-border" data-testid={`ptype-${t.replace(/\W+/g, "-")}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <Buildings size={16} weight="duotone" className="text-accent shrink-0" />
                  <span className="text-sm text-primary truncate">{t}</span>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-xs text-muted-foreground tabular">{counts[t] || 0} jobs</span>
                  <button className="text-destructive" onClick={() => persist(items.filter((x) => x !== t))} data-testid={`delete-ptype-${t.replace(/\W+/g, "-")}`}><Trash size={15} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="p-5 border-border bg-muted/40">
        <div className="flex items-start gap-3">
          <ArrowsClockwise size={18} className="text-accent mt-0.5 shrink-0" />
          <p className="text-sm text-muted-foreground">
            Removing a type here does not change jobs already tagged with it — existing reports keep their label. New jobs will only offer the types listed above.
          </p>
        </div>
      </Card>
    </div>
  );
}
